import { Avatar } from 'antd';
import {
    AgeVerificationStatus,
    Client,
    DeviceAppVersion,
    UserConsent,
} from '../../stores/client';
import { TopTabs } from '../top-tabs';
import { Tag } from '../tag';
import style from './style.module.scss';
import CloseIcon from '../../icons/X.svg?react';
import moment from 'moment';
import { UserDetailsTab } from './user-details-tab';
import CheckIcon from '../../icons/CheckCircle.svg?react';
import EmptyCheckIcon from '../../icons/CheckCircleEmpty.svg?react';
import IdentificationCardIcon from '../../icons/IdentificationCard.svg?react';
import PhoneIcon from '../../icons/Phone.svg?react';
import EnvelopeSimpleIcon from '../../icons/EnvelopeSimple.svg?react';
import * as Colors from '../../styles/colors';
import { UserCouponsTab } from './user-coupons-tab';
import { UserCampaignActivityTab } from './user-campaign-activity-tab';
import WashCodeRecordsTab from './user-wash-code-tab';

type UserProfileProps = {
    onCloseClick: () => void;
    clientObject?: Client;
    resetPassword?: () => void;
    paymentMethod?: {
        loading: boolean;
        data: string | null;
    };
    deviceAppVersion?: {
        loading: boolean;
        data: DeviceAppVersion | null;
    };
    userConsents?: UserConsent[] | null;
    onUpdateConsentStatus?: (
        userConsentRecordId: number,
        status: string
    ) => void;
    isUpdatingConsentStatus?: boolean;
    hasManagementAccess?: boolean;
};

export const UserProfile = ({
    onCloseClick,
    clientObject,
    resetPassword,
    paymentMethod,
    deviceAppVersion,
    userConsents,
    onUpdateConsentStatus,
    isUpdatingConsentStatus,
    hasManagementAccess,
}: UserProfileProps) => {
    return (
        <>
            <div className={style.container}>
                <div className={style.header}>
                    <div className={style.close} onClick={onCloseClick}>
                        <CloseIcon />
                    </div>
                    <div>
                        CLIENT #{clientObject?.id}{' '}
                        <span className={style.deleted_user}>
                            {clientObject?.deletedAt !== null && (
                                <Tag variant="error">
                                    This is a deleted user
                                </Tag>
                            )}
                        </span>
                    </div>
                    <div className={style.user_info}>
                        <Avatar
                            shape="circle"
                            style={{
                                verticalAlign: 'middle',
                                backgroundColor: Colors.RED_500,
                            }}
                            size={64}
                            src={clientObject?.picture ?? ''}
                        >
                            {clientObject?.firstName
                                ?.charAt(0)
                                .toLocaleUpperCase()}
                            {clientObject?.lastName
                                ?.charAt(0)
                                .toLocaleUpperCase()}
                        </Avatar>
                        <div className={style.user_info_and_contact}>
                            <div className={style.user_title}>
                                {clientObject?.birthday
                                    ? `${clientObject?.firstName} ${
                                          clientObject?.lastName
                                      } (${moment().diff(
                                          clientObject?.birthday,
                                          'years'
                                      )})`
                                    : `${clientObject?.firstName} ${clientObject?.lastName}`}
                                {clientObject?.language && (
                                    <Tag
                                        variant={
                                            clientObject?.language === 'fr'
                                                ? 'error'
                                                : 'orange'
                                        }
                                    >
                                        {clientObject?.language}
                                    </Tag>
                                )}
                            </div>
                            <div className={style.user_contact}>
                                <div className={style.age_verification}>
                                    <IdentificationCardIcon />
                                    {clientObject?.ageVerificationStatus ===
                                        AgeVerificationStatus.VERIFIED ||
                                    clientObject?.ageVerificationStatus ===
                                        AgeVerificationStatus.INSUFFICIENT
                                        ? `${clientObject.maxAgeVerified}+`
                                        : 'N/A'}
                                    {clientObject?.ageVerificationStatus ===
                                        AgeVerificationStatus.VERIFIED && (
                                        <div className={style.confirmed}>
                                            <CheckIcon />
                                        </div>
                                    )}
                                </div>
                                {clientObject?.phoneNumber && (
                                    <div className={style.phone_number}>
                                        <PhoneIcon />
                                        {clientObject.phoneNumber}
                                        <div className={style.confirmed}>
                                            {clientObject.isPhoneNumberConfirmed ? (
                                                <CheckIcon />
                                            ) : (
                                                <EmptyCheckIcon stroke="grey" />
                                            )}
                                        </div>
                                    </div>
                                )}
                                {clientObject?.email && (
                                    <div className={style.email}>
                                        <EnvelopeSimpleIcon />
                                        {clientObject.email}
                                        <div className={style.confirmed}>
                                            {clientObject.isEmailConfirmed ? (
                                                <CheckIcon />
                                            ) : (
                                                <EmptyCheckIcon stroke="grey" />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <TopTabs
                    tabsArray={[
                        {
                            name: 'Details',
                            key: 'user_details',
                            children: (
                                <UserDetailsTab
                                    clientObject={clientObject}
                                    resetPassword={resetPassword}
                                    paymentMethod={paymentMethod}
                                    deviceAppVersion={deviceAppVersion}
                                    userConsents={userConsents}
                                    onUpdateConsentStatus={
                                        onUpdateConsentStatus
                                    }
                                    isUpdatingConsentStatus={
                                        isUpdatingConsentStatus
                                    }
                                />
                            ),
                        },
                        {
                            name: 'Coupons',
                            key: 'user_coupons',
                            children: (
                                <>
                                    <UserCouponsTab
                                        clientObject={clientObject}
                                    />
                                </>
                            ),
                        },
                        {
                            name: 'Campaign activity',
                            key: 'campaign_activity',
                            children: (
                                <UserCampaignActivityTab
                                    // Remounted per client rather than reset
                                    // internally: the drawer keeps every pane
                                    // alive, and a fresh instance drops the
                                    // previous client's rows, filters and any
                                    // in-flight request by construction.
                                    key={clientObject?.id}
                                    clientObject={clientObject}
                                />
                            ),
                        },
                        ...(hasManagementAccess
                            ? [
                                  {
                                      name: 'Car Wash Code',
                                      key: 'wash_code_records',
                                      children: (
                                          <WashCodeRecordsTab
                                              clientObject={clientObject}
                                          />
                                      ),
                                  },
                              ]
                            : []),
                    ]}
                />
            </div>
        </>
    );
};
